import apiService from './api';
import { API_BASE_URL } from '../config/env';

// =====================================================
// Types
// =====================================================

export interface ImageUploadResult {
  url: string;
  key: string;
  size: number;
  mimetype: string;
}

interface UploadResponse {
  status: 'success' | 'error';
  message?: string;
  data: {
    url?: string;
    key?: string;
    size?: number;
    mimetype?: string;
    images?: ImageUploadResult[];
  };
}

export type ImageCategory =
  | 'products'
  | 'thumbnails'
  | 'avatars'
  | 'stores'
  | 'stories'
  | 'chat'
  | 'kyc'
  | 'banners'
  | 'tickets'
  | 'accounting'
  | 'blog-ads';

// =====================================================
// Helper Functions
// =====================================================

const isBase64 = (str: string): boolean => {
  return str.startsWith('data:image');
};

const isUrl = (str: string): boolean => {
  return str.startsWith('http://') || str.startsWith('https://');
};

// =====================================================
// Image Upload Service
// =====================================================

class ImageUploadService {
  /**
   * آپلود یک تصویر به ArvanCloud
   */
  async uploadImage(file: File, category: ImageCategory = 'products'): Promise<ImageUploadResult | null> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(
        `${API_BASE_URL}/upload/${category}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: formData,
        }
      );

      const data: UploadResponse = await response.json();

      if (data.status === 'success' && data.data.url) {
        return {
          url: data.data.url,
          key: data.data.key || '',
          size: data.data.size || 0,
          mimetype: data.data.mimetype || file.type,
        };
      }

      throw new Error(data.message || 'خطا در آپلود تصویر');
    } catch (error) {
      console.error('خطا در آپلود تصویر:', error);
      return null;
    }
  }

  /**
   * آپلود چند تصویر به ArvanCloud
   */
  async uploadImages(files: File[], category: ImageCategory = 'products'): Promise<ImageUploadResult[]> {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images', file);
      });

      const response = await fetch(
        `${API_BASE_URL}/upload/${category}/multiple`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: formData,
        }
      );

      const data: UploadResponse = await response.json();

      if (data.status === 'success' && data.data.images) {
        return data.data.images;
      }

      throw new Error(data.message || 'خطا در آپلود تصاویر');
    } catch (error) {
      console.error('خطا در آپلود تصاویر:', error);
      return [];
    }
  }

  /**
   * حذف تصویر از ArvanCloud
   */
  async deleteImage(keyOrUrl: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: isUrl(keyOrUrl) ? undefined : keyOrUrl,
          url: isUrl(keyOrUrl) ? keyOrUrl : undefined,
        }),
      });

      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('خطا در حذف تصویر:', error);
      return false;
    }
  }

  /**
   * اعتبارسنجی فایل‌ها قبل از آپلود
   */
  validateFiles(
    files: File[],
    options?: { maxSize?: number; maxFiles?: number }
  ): {
    validFiles: File[];
    errors: string[];
  } {
    const maxSize = options?.maxSize || 5 * 1024 * 1024; // 5MB default
    const maxFiles = options?.maxFiles || 20;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    const validFiles: File[] = [];
    const errors: string[] = [];

    if (files.length > maxFiles) {
      errors.push(`حداکثر ${maxFiles} فایل می‌توانید آپلود کنید`);
      return { validFiles: [], errors };
    }

    files.forEach((file, index) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`فایل ${file.name}: فرمت غیرمجاز (فقط JPG، PNG، WEBP و GIF)`);
        return;
      }

      if (file.size > maxSize) {
        const maxSizeMB = Math.round(maxSize / (1024 * 1024));
        errors.push(`فایل ${file.name}: حجم بیش از ${maxSizeMB}MB`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errors };
  }

  /**
   * تبدیل فایل به Base64 (فقط برای موارد خاص)
   * @deprecated از این متد استفاده نکنید - به جای آن از uploadImage استفاده کنید
   */
  async fileToBase64(file: File): Promise<string> {
    console.warn('fileToBase64 is deprecated. Use uploadImage instead for ArvanCloud storage.');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * بررسی اینکه آیا رشته یک URL معتبر ArvanCloud است
   */
  isArvanCloudUrl(url: string): boolean {
    if (!url) return false;
    return (
      url.includes('.arvanstorage.ir') ||
      url.includes('.parspack.net') ||
      url.includes('api.gamizo.ir/media/') ||
      url.includes('cdn.gamizo.ir')
    );
  }

  /**
   * پردازش تصاویر موجود (تشخیص URL از Base64)
   */
  processExistingImages(images: string[]): { urls: string[]; base64Images: string[] } {
    const urls: string[] = [];
    const base64Images: string[] = [];

    images.forEach((img) => {
      if (isUrl(img)) {
        urls.push(img);
      } else if (isBase64(img)) {
        base64Images.push(img);
      }
    });

    return { urls, base64Images };
  }
}

// Export singleton instance
const imageUploadService = new ImageUploadService();
export default imageUploadService;
